import React, { useContext, useEffect, useRef, useState } from "react";
import { ConfiguracoesContext } from "../../../contexts/Configuracoes/ConfiguracoesContext";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { Select } from "../../Select";
import { ToggleConfigurar } from "../../Toggle/ToggleConfigurar/ToggleConfigurar";
import { mensageriaApi, ConexaoStatus } from "../../../hooks/mensageriaApi";
import { ReactComponent as InfoSVG } from "../../../assets/newImages/icons/info.svg";
import ModalInfoMensagens from "../../Modal/ModalInfoMensagens";
import Modal from "../../Modal/Modal";
import CuponsMain from "../cuponsMain/CuponsMain";
import Table from "../../Table/Table";
import { MensageriaContainer } from "./styles";

const delayOptions = [
  { name: "30 minutos", value: "30" },
  { name: "1 hora", value: "60" },
  { name: "2 horas", value: "120" },
  { name: "4 horas", value: "240" },
  { name: "24 horas", value: "1440" },
];

// Templates fixos exibidos na previa.
// cupom = nome do cupom; desconto = texto ja formatado ("10% de desconto" / "R$ 10,00 de desconto").
const templatesMensagem = [
  {
    id: "1",
    build: (cupom: string, desconto: string) =>
      "Oi, [Primeiro nome]. Tudo bem? Os produtos que voce escolheu estao te esperando, volte e finalize." +
      (cupom ? `\n\n🎫 Aproveite o cupom ${cupom} e garanta ${desconto}!` : "") +
      "\n[link da sacola]",
  },
  {
    id: "2",
    build: (cupom: string, _desconto: string) =>
      "Ei, [Primeiro nome] vi voce passar aqui na [nome da marca]. Acabou esquecendo os produtos na sacola.\n\nPara garantir os seus produtos, clique no link abaixo e finalize o pedido.\n[link da sacola]" +
      (cupom ? `\n\n🎁 Ah, e tem cupom de desconto para voce: ${cupom}` : ""),
  },
  {
    id: "3",
    build: (cupom: string, desconto: string) =>
      "Oi, [Primeiro nome]. Tudo bem? Os produtos que voce escolheu estao te esperando, para garantir eles, voce precisa voltar e finalizar o pedido o mais rapido possivel, ta?" +
      (cupom ? `\n\nAproveite o cupom ${cupom} e garanta ${desconto}!` : "") +
      "\n🔗 [link da sacola]",
  },
];

// Formata o desconto do cupom igual ao backend (desconto armazenado x100).
const formatarDesconto = (cupom?: any): string => {
  if (!cupom) return "";
  const valor = (Number(cupom.desconto) || 0) / 100;
  if (cupom.tipo_desconto === "valor") {
    return `R$ ${valor.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} de desconto`;
  }
  // Porcentagem: remove casas decimais sobrando (10,00 -> 10 ; 15,50 -> 15,5)
  const s = valor.toFixed(2).replace(".", ",").replace(/0+$/, "").replace(/,$/, "");
  return `${s}% de desconto`;
};

// Opcao para nao oferecer cupom (oculta o bloco de cupom nas previas)
const cupomNenhum = { name: "Não oferecer cupom", value: "" };

// Formata o numero do WhatsApp (ex.: "555434435308@c.us" -> "+55 (54) 3443-5308")
const formatarNumero = (numero?: string | null): string => {
  if (!numero) return "";
  const d = numero.split("@")[0].replace(/\D/g, "");
  if (d.length >= 12) {
    const ddi = d.slice(0, 2);
    const ddd = d.slice(2, 4);
    const rest = d.slice(4);
    const meio = rest.length > 8 ? rest.slice(0, 5) : rest.slice(0, 4);
    const fim = rest.length > 8 ? rest.slice(5) : rest.slice(4);
    return `+${ddi} (${ddd}) ${meio}-${fim}`;
  }
  return d ? `+${d}` : "";
};

const Mensageria: React.FC = () => {
  const { configuracoes, setConfiguracoes, cupons } =
    useContext(ConfiguracoesContext);
  const { theme } = useTheme();
  const apiRef = useRef(mensageriaApi());
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [conexao, setConexao] = useState<ConexaoStatus | null>(null);
  const [conectando, setConectando] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrExpirado, setQrExpirado] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [cupomModalOpen, setCupomModalOpen] = useState(false);
  const [enviandoTeste, setEnviandoTeste] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState<any>(null);

  // Opcoes do select vindas dos cupons cadastrados (mesma fonte do CuponsMain).
  // So cupons "perenes": sem data de expiracao e sem limite de uso, pois a
  // recuperacao de sacola e continua e nao faz sentido oferecer cupom que expira/esgota.
  const cupomOptions = (cupons || [])
    .filter((c: any) => {
      const ativo =
        c.ativo === true ||
        c.ativo === 1 ||
        c.ativo === "1" ||
        c.ativo === "true";
      const semExpiracao = c.dataExpiracao == null || c.dataExpiracao === "";
      const semLimite = c.quantidadeLimite == null || c.quantidadeLimite === 0;
      return ativo && semExpiracao && semLimite;
    })
    .map((c: any) => ({ name: c.cupom, value: c.cupom }));

  const whatsapp = configuracoes.comunicacao?.whatsapp;
  const whatsappAtivo = whatsapp?.ativo || false;
  const cesta = whatsapp?.cesta_abandonada;
  const cestaAtiva = cesta?.ativo || false;
  const delayMinutos = cesta?.delay_minutos || 60;
  const cupomSelecionado = cesta?.cupom || "";
  const descontoSelecionado = formatarDesconto(
    (cupons || []).find((c: any) => c.cupom === cupomSelecionado)
  );

  useEffect(() => {
    return () => {
      pararPolling();
      pararTimerExpiracao();
    };
  }, []);

  useEffect(() => {
    if (whatsappAtivo) verificarStatus();
  }, [whatsappAtivo]);

  const verificarStatus = async () => {
    const data = await apiRef.current.verificarStatus();
    if (data) {
      setConexao(data);
      if (data.conectado) pararPolling();
    }
  };

  const iniciarConexao = async () => {
    setConectando(true);
    setQrExpirado(false);
    const data = await apiRef.current.conectarWhatsApp();
    setConectando(false);
    console.log("[WAHA conectar]", data, (data as any)?.debug);
    if (!data) return;
    if (data.status === "WORKING") {
      setConexao({ conectado: true, status: "WORKING", numero: null, nome: null });
      setQrCode(null);
      return;
    }
    if (data.qr_code) {
      setQrCode(data.qr_code);
      setQrExpirado(false);
      iniciarPolling();
      iniciarTimerExpiracao();
    }
  };

  const iniciarPolling = () => {
    pararPolling();
    pollingRef.current = setInterval(async () => {
      const status = await apiRef.current.verificarStatus();
      if (status?.conectado) {
        setConexao(status);
        setQrCode(null);
        pararPolling();
        pararTimerExpiracao();
      }
    }, 3000);
  };

  const pararPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Expira o QR apos 2 minutos: para o polling e pede para gerar um novo.
  const iniciarTimerExpiracao = () => {
    pararTimerExpiracao();
    qrTimerRef.current = setTimeout(() => {
      setQrExpirado(true);
      setQrCode(null);
      pararPolling();
    }, 120000);
  };

  const pararTimerExpiracao = () => {
    if (qrTimerRef.current) {
      clearTimeout(qrTimerRef.current);
      qrTimerRef.current = null;
    }
  };

  const testarEnvio = async () => {
    setEnviandoTeste(true);
    setResultadoTeste(null);
    const data = await apiRef.current.testarEnvio();
    setResultadoTeste(data);
    setEnviandoTeste(false);
  };

  const updateWhatsappConfig = (updates: Record<string, any>) => {
    setConfiguracoes({
      ...configuracoes,
      comunicacao: {
        ...configuracoes.comunicacao,
        whatsapp: {
          ...configuracoes.comunicacao?.whatsapp,
          ativo: whatsappAtivo,
          ...updates,
        },
      },
    });
  };

  // Mescla campos da cesta_abandonada preservando os demais.
  const updateCesta = (updates: Record<string, any>) => {
    updateWhatsappConfig({
      cesta_abandonada: {
        ativo: cestaAtiva,
        delay_minutos: delayMinutos,
        cupom: cupomSelecionado,
        ...updates,
      },
    });
  };

  return (
    <MensageriaContainer theme={theme}>
      <div className="descricao-texto fonteTexto">
        Esta integração utiliza uma conexão não oficial com o WhatsApp. O envio
        excessivo ou fora das boas práticas pode ocasionar limitações
        temporárias ou permanentes no número conectado.
      </div>

      {whatsappAtivo && (
        <>
          {qrCode && !conexao?.conectado && (
            <div className="qr-container">
              <p className="qr-instrucao fonteRotulos">
                Abra o WhatsApp no celular, vá em{" "}
                <strong>Aparelhos conectados</strong> e escaneie o código abaixo:
              </p>
              <img src={qrCode} alt="QR Code WhatsApp" className="qr-image" />
              <p className="qr-aguardando fonteRotulos">
                Aguardando leitura do QR Code...
              </p>
            </div>
          )}

          {qrExpirado && !conexao?.conectado && (
            <p className="qr-expirado fonteRotulos">
              O QR Code expirou. Gere um novo para conectar.
            </p>
          )}

          {conexao?.conectado && (
            <div className="conectado-tabela">
              <Table
                columns={[
                  { key: "numero", label: "Número" },
                  { key: "nome", label: "Nome" },
                  {
                    key: "status",
                    label: "Status",
                    mask: (value: any) => (
                      <span className="badge-conectado">{value}</span>
                    ),
                  },
                ]}
                data={[
                  {
                    numero: formatarNumero(conexao.numero),
                    nome: conexao.nome || "-",
                    status: "Conectado",
                  },
                ]}
                sortable={false}
                selectable={false}
                button={false}
                page={1}
                itensPorPagina={10}
              />
            </div>
          )}

          {!conexao?.conectado && (
            <button
              className="btn-conectar fonteTexto"
              onClick={iniciarConexao}
              disabled={conectando}
            >
              {conectando
                ? "Conectando..."
                : qrCode || qrExpirado
                ? "Gerar novo QR Code"
                : "Conectar WhatsApp"}
            </button>
          )}

          <div className="divisor" />

          {/* Recuperacao de Sacola Abandonada */}
          <div className="sacola-header">
            <span className="sacola-titulo fonteTexto">
              Recuperação de Sacola Abandonada
            </span>
            <ToggleConfigurar
              value={cestaAtiva}
              onChange={(newValue) => updateCesta({ ativo: newValue })}
              onConfigurar={() => {}}
            />
          </div>

          <div className="descricao-texto descricao-sutil fonteTexto">
            Mensagens automáticas ajudam a recuperar vendas de clientes que
            abandonaram o checkout.
          </div>

          {cestaAtiva && (
            <>
              <div className="input-container grid-campos">
                <Select
                  label="Tempo para envio após abandono"
                  alignLabel="left"
                  sizeLabel="small"
                  options={delayOptions}
                  value={delayMinutos.toString()}
                  onChange={(value) =>
                    updateCesta({ delay_minutos: parseInt(value) })
                  }
                />
                <Select
                  label="Disponibilizar cupom de desconto"
                  alignLabel="left"
                  sizeLabel="small"
                  options={[
                    cupomNenhum,
                    ...cupomOptions,
                    { name: "+ Criar e editar cupons", value: "editar" },
                  ]}
                  value={cupomSelecionado}
                  onChange={(value) => {
                    if (value === "editar") {
                      setCupomModalOpen(true);
                    } else {
                      updateCesta({ cupom: value });
                    }
                  }}
                />
              </div>

              <div className="previa">
                <div className="previa-label fonteTexto">
                  <span>Prévia das mensagens</span>
                  <InfoSVG
                    style={{
                      width: 16,
                      height: 16,
                      marginLeft: 8,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setInfoOpen(true);
                    }}
                  />
                </div>
                <div className="previa-cards">
                  {templatesMensagem.map((t) => (
                    <div key={t.id} className="previa-card fonteRotulo">
                      {t.build(cupomSelecionado, descontoSelecionado)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="teste-envio">
                <button
                  className="btn-testar fonteTexto"
                  onClick={testarEnvio}
                  disabled={enviandoTeste || !conexao?.conectado}
                >
                  {enviandoTeste ? "Enviando..." : "Testar envio"}
                </button>
                {!conexao?.conectado && (
                  <span className="teste-hint fonteRotulos">
                    Conecte o WhatsApp para testar o envio.
                  </span>
                )}
                {resultadoTeste && (
                  <div
                    className={`resultado-teste fonteRotulos ${
                      resultadoTeste.sucesso ? "sucesso" : "erro"
                    }`}
                  >
                    {resultadoTeste.erro
                      ? resultadoTeste.erro
                      : resultadoTeste.sucesso
                      ? `Mensagem enviada para ${resultadoTeste.destinatario}`
                      : "Falha no envio"}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      <ModalInfoMensagens isOpen={infoOpen} setModal={setInfoOpen} />

      <Modal
        isOpen={cupomModalOpen}
        setModal={setCupomModalOpen}
        width="100rem"
        maxWidth="98vw"
        padding="3rem 4rem"
      >
        <CuponsMain mostrarTexto={false} apenasPerenes={true} />
      </Modal>
    </MensageriaContainer>
  );
};

export default Mensageria;
